// Use unified date utils
import { moment, SYSTEM_TIMEZONE } from '../../../utils/dateUtils';

// Default work hours (will be replaced with company settings)
const DEFAULT_START_HOUR = 7;
const DEFAULT_END_HOUR = 22;

/**
 * Generate time slots array based on work hours
 * @param {number} startHour - Start hour (default 7)
 * @param {number} endHour - End hour (default 22)
 * @returns {Array} Array of time slot objects
 */
export const generateTimeSlots = (startHour = DEFAULT_START_HOUR, endHour = DEFAULT_END_HOUR) => {
  const slots = [];
  for (let hour = startHour; hour <= endHour; hour++) {
    const hour12 = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const time12 = `${hour12}:00 ${ampm}`; // No leading zero for display
    const time24 = `${hour.toString().padStart(2, '0')}:00`;
    
    slots.push({
      index: hour - startHour,
      time12,
      time24,
      hour,
    });
  }
  return slots;
};

/**
 * Get time string by index (backwards compatible)
 * @param {number} index - Slot index
 * @param {number} startHour - Start hour
 * @returns {string} Time in 12-hour format
 */
export const defineTime = (index, startHour = DEFAULT_START_HOUR) => {
  const slots = generateTimeSlots(startHour);
  return slots[index]?.time12 || '7:00 AM';
};

/**
 * Convert 12-hour time to 24-hour format using moment
 * @param {string} time12 - Time in 12-hour format (e.g., "07:00 AM")
 * @returns {object} Object with hour and minute
 */
export const convertTime = (time12) => {
  const parsed = moment(time12, 'hh:mm A');
  return {
    h: parsed.format('HH'),
    m: parsed.format('mm'),
  };
};

/**
 * Get slot index from time string
 * @param {string} time12 - Time in 12-hour format
 * @param {number} startHour - Start hour
 * @returns {number} Slot index
 */
export const getSlotIndexFromTime = (time12, startHour = DEFAULT_START_HOUR) => {
  const parsed = moment(time12, 'hh:mm A');
  const hour = parsed.hour();
  return hour - startHour;
};

/**
 * Check if appointment time falls within preferred time window
 * @param {object} appointment - Appointment object
 * @param {string} newStartTime - New start time (ISO)
 * @param {string} newEndTime - New end time (ISO)
 * @returns {boolean} True if within preferred time window
 */
export const matchesPreferredTime = (appointment, newStartTime, newEndTime) => {
  // No preferred time set - always matches
  if (!appointment.preferredDate && !appointment.preferredStartTime && !appointment.preferredEndTime) {
    return true;
  }

  const newStart = moment(newStartTime);
  const newEnd = moment(newEndTime);

  // Check date - must match exactly
  if (appointment.preferredDate) {
    const prefDate = moment(appointment.preferredDate).format('YYYY-MM-DD');
    const newDate = newStart.format('YYYY-MM-DD');
    if (prefDate !== newDate) {
      return false;
    }
  }

  // If both preferred start and end times are set, check if new time is within the window
  if (appointment.preferredStartTime && appointment.preferredEndTime) {
    // Get preferred times in the same date context as new appointment
    const prefStart = moment(newStart).set({
      hour: moment(appointment.preferredStartTime).hour(),
      minute: moment(appointment.preferredStartTime).minute(),
      second: 0
    });
    const prefEnd = moment(newStart).set({
      hour: moment(appointment.preferredEndTime).hour(),
      minute: moment(appointment.preferredEndTime).minute(),
      second: 0
    });

    // New start must be >= preferred start AND new end must be <= preferred end
    if (newStart.isBefore(prefStart) || newEnd.isAfter(prefEnd)) {
      return false;
    }
  } else {
    // Only preferred start time set - new start must be >= preferred start
    if (appointment.preferredStartTime && !appointment.preferredEndTime) {
      const prefStartHour = moment(appointment.preferredStartTime).hour();
      const prefStartMin = moment(appointment.preferredStartTime).minute();
      const prefStart = moment(newStart).set({
        hour: prefStartHour,
        minute: prefStartMin,
        second: 0
      });
      
      if (newStart.isBefore(prefStart)) {
        return false;
      }
    }

    // Only preferred end time set - new end must be <= preferred end
    if (appointment.preferredEndTime && !appointment.preferredStartTime) {
      const prefEndHour = moment(appointment.preferredEndTime).hour();
      const prefEndMin = moment(appointment.preferredEndTime).minute();
      const prefEnd = moment(newStart).set({
        hour: prefEndHour,
        minute: prefEndMin,
        second: 0
      });
      
      if (newEnd.isAfter(prefEnd)) {
        return false;
      }
    }
  }

  return true;
};

/**
 * Format preferred time info for display
 * @param {object} appointment - Appointment object
 * @returns {string} Formatted preferred time string
 */
export const formatPreferredTime = (appointment) => {
  const parts = [];
  
  if (appointment.preferredDate) {
    parts.push(`Date: ${moment(appointment.preferredDate).format('MM/DD/YYYY')}`);
  }
  
  if (appointment.preferredStartTime) {
    parts.push(`Start: ${moment(appointment.preferredStartTime).tz(SYSTEM_TIMEZONE).format('h:mm A')}`);
  }
  
  if (appointment.preferredEndTime) {
    parts.push(`End: ${moment(appointment.preferredEndTime).tz(SYSTEM_TIMEZONE).format('h:mm A')}`);
  }
  
  return parts.length > 0 ? parts.join(', ') : 'Not set';
};

/**
 * Sort appointments by column
 * @param {object} col - Column object
 * @param {Array} arr - Appointments array
 * @param {number} startHour - Work day start hour
 */
export const sortAppointmentsByColumn = (col, arr, startHour = DEFAULT_START_HOUR) => {
  arr.forEach((item) => {
    const time12 = moment(item.startTime).format('h:mm A');
    const slotTime = moment(item.startTime).format('h') + ':00 ' + moment(item.startTime).format('A');
    
    col.items.find((el, elIdx) => {
      if (el.time === slotTime) {
        col.items[elIdx] = {
          ...item,
          time: el.time,
        };
        return true;
      }
      return false;
    });
  });
};
